import { liveVoiceMessageCallValue } from "./liveVoiceMessageCall.ts";
import { frozenSessionToolsValue } from "./frozenSessionTools.ts";
export { frozenSessionToolsValue, validFrozenSessionTools } from "./frozenSessionTools.ts";
export { liveVoiceMessageCallValue } from "./liveVoiceMessageCall.ts";
import { contextSelectionDigestValue } from "./contextSelection.ts";
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex } from "@noble/hashes/utils";

import { encodeWorkingContextBitmap, type MessageContextResolution } from "@cleo/contracts";

type ResolutionBody<T = MessageContextResolution> = T extends MessageContextResolution
  ? Omit<T, "digest">
  : never;

/**
 * The canonical digest body of one AgentContextResolution: every identity
 * field EXCEPT the digest itself, serialized with a fixed key order so the
 * same frozen boundary always yields the same bytes on every surface.
 * Rendered in-set, provider-delivered set, and MCP-enforced set prove parity
 * by recomputing THIS (O17).
 */
export function agentContextResolutionBody(resolution: ResolutionBody): string {
  if (resolution.schemaVersion === "local-context-resolution-v1")
    return JSON.stringify({
      schemaVersion: resolution.schemaVersion,
      resolutionId: resolution.resolutionId,
      worldKey: null,
      roomId: null,
      sessionId: resolution.sessionId,
      projectId: resolution.projectId,
      agentEnvironmentId: resolution.agentEnvironmentId,
      agentThreadId: resolution.agentThreadId,
      selectionDigest: resolution.selectionDigest,
      selectionCut: resolution.selectionCut,
      sourceResolutionId: resolution.sourceResolutionId,
      offlineCopy: resolution.offlineCopy,
      tools: resolution.tools,
      expressions: resolution.expressions,
      memberIds: resolution.memberIds,
      createdMemberIds: resolution.createdMemberIds,
      workingContextBitmap: resolution.workingContextBitmap,
      frozenAt: resolution.frozenAt,
    });
  return JSON.stringify({
    schemaVersion: resolution.schemaVersion,
    resolutionId: resolution.resolutionId,
    worldKey: resolution.worldKey,
    roomId: resolution.roomId,
    sessionId: resolution.sessionId,
    agentEnvironmentId: resolution.agentEnvironmentId,
    agentThreadId: resolution.agentThreadId,
    catalogDigest: resolution.catalogDigest,
    catalogCustodyDigest: resolution.catalogCustodyDigest,
    selectionDigest: resolution.selectionDigest,
    selectionCut: resolution.selectionCut,
    memberIds: resolution.memberIds,
    workingContextBitmap: resolution.workingContextBitmap,
    expressions: contextSelectionDigestValue(resolution.expressions),
    toolSelection: frozenSessionToolsValue(resolution.toolSelection),
    liveCall: liveVoiceMessageCallValue(resolution.liveCall),
    frozenAt: resolution.frozenAt,
  });
}

export function digestAgentContextResolution(resolution: ResolutionBody): string {
  return bytesToHex(sha256(new TextEncoder().encode(agentContextResolutionBody(resolution))));
}

/**
 * Independent readback (O17/O30): recompute the digest from the stored body
 * and compare. A mismatch is a typed refusal reason, never a repair.
 */
export function agentContextResolutionDigestMatches(resolution: MessageContextResolution): boolean {
  return digestAgentContextResolution(resolution) === resolution.digest;
}

/** A Local continuation receives reviewed choices, never the source's implicit creation reach. */
export function retargetMessageContextResolution(
  resolution: MessageContextResolution,
  agentThreadId: string,
): MessageContextResolution {
  if (resolution.agentThreadId === agentThreadId) return resolution;
  if (
    resolution.schemaVersion === "local-context-resolution-v1" &&
    resolution.createdMemberIds?.length
  ) {
    const memberIds = resolution.memberIds.filter(
      (id) =>
        !(resolution.createdMemberIds ?? []).includes(id) ||
        resolution.expressions.some((e) => e.selected && e.memberId === id),
    );
    const {
      digest: _digest,
      workingContextBitmap: _bitmap,
      createdMemberIds: _created,
      ...original
    } = resolution;
    const workingContextBitmap = encodeWorkingContextBitmap(
      memberIds,
      new Set(
        resolution.expressions
          .filter((e) => e.selected && e.delivery === "include")
          .map((e) => e.memberId),
      ),
    );
    const body = {
      ...original,
      agentThreadId,
      memberIds,
      ...(workingContextBitmap ? { workingContextBitmap } : {}),
    };
    return { ...body, digest: digestAgentContextResolution(body) };
  }
  const body = { ...resolution, agentThreadId };
  return { ...body, digest: digestAgentContextResolution(body) };
}
